/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useContext } from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { Cell } from "react-table";
import { Box, Text } from "@nordcloud/gnui";
import { FlexContainer, Table } from "~/components";
import { FCPatchPlanAdd } from "~/components/Applications/ApplicationDetails/FCPPlanAdd";
import { FCPatchPlanRemove } from "~/components/Applications/ApplicationDetails/FCPPlanRemove";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { DetailRow } from "~/components/Utils/DetailRow";
import { FCPatchPlan } from "~/models";
import { ApplicationViewCtx } from "~/views/customer/applications/ApplicationViewCtx";

function FCPatchPlans() {
  const { application, updateApplication, canEdit } = useContext(
    ApplicationViewCtx
  );
  const plans = application.fcPatchPlans;

  const addPlan = React.useCallback(
    (newPlan: FCPatchPlan) =>
      updateApplication({ fcPatchPlans: [...plans, newPlan] }),
    [plans, updateApplication]
  );

  const removePlan = React.useCallback(
    (idx) =>
      updateApplication({
        fcPatchPlans: plans.filter((_, i) => i !== idx),
      }),
    [plans, updateApplication]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Customer name",
        accessor: "customerName",
      },
      {
        Header: "Plan",
        accessor: "plan",
        Cell: ({ value }: Cell<FCPatchPlan>) => (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
      },
      {
        Header: "Environments",
        accessor: "environments",
        Cell: ({ value }: Cell<FCPatchPlan>) => value?.join(", ") || "–",
      },
      {
        Header: "Schedule",
        accessor: "cron",
        Cell: ({ value }: Cell<FCPatchPlan>) => value || "–",
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }: Cell<FCPatchPlan>) => {
          return (
            canEdit && (
              <FCPatchPlanRemove
                removeCallback={removePlan}
                plan={row?.original}
                idx={row.index}
              />
            )
          );
        },
      },
    ],
    [canEdit, removePlan]
  );

  const environments = application.environments.map((env) => env.name);

  return (
    <Box spacing="spacing04">
      <Row>
        <Col lg={12}>
          <FlexContainer>
            <Text weight="medium">Fleet Control Patching plans</Text>
            <FCPatchPlanAdd environments={environments} addPlan={addPlan} />
          </FlexContainer>
          <DetailRow title="Manual Patching">
            <InlineEditText
              name="manualPatching"
              defaultValue={application.manualPatching}
              onSubmit={(val) => updateApplication({ manualPatching: val })}
              canEdit={canEdit}
            />
          </DetailRow>
          <Table columns={columns} data={plans} mb="0" />
        </Col>
      </Row>
    </Box>
  );
}

export { FCPatchPlans };
